/**
 * Sign-in: company code first, then credentials.
 *
 * The company is asked for before the password because branding, language, and even
 * which server-side rules apply all depend on it. A dispatcher sees their own firm's
 * name and colour before typing anything secret, which is also the honest order — the
 * password is meaningless without knowing where it is being sent.
 *
 * One centred card on the ground, 360px wide, and the same card the second factor and the
 * invitation page use. It renders before `/api/tenant/branding` has answered anything, so
 * it themes from the stored preference alone: the tokens are in the stylesheet and
 * `--brand-primary` has a per-scheme default, which is the whole reason this screen does
 * not wait for a firm.
 */

import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { ApiError, errorKey } from '@taxi/api-client';

import { useAuth } from '../auth/authContext';
import { SecondFactor, type MfaChallenge } from '../auth/SecondFactor';
import { Banner, Button, Card, Field, Input, PageHeader } from '../ui';

export function SignIn() {
  const { t } = useTranslation();
  const { branding, sessionExpired, resolveTenant, signIn, completeSecondFactor } = useAuth();

  const [company, setCompany] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * The half-finished sign-in, when the account owes a second factor.
   *
   * Held here rather than in the provider because it is a screen rather than a session:
   * abandoning it leaves nothing behind, which is exactly what a challenge should do.
   */
  const [challenge, setChallenge] = useState<MfaChallenge | null>(null);

  const wantsCredentials = branding !== null;

  async function run(action: () => Promise<MfaChallenge | null | void>) {
    setBusy(true);
    setError(null);

    try {
      const owed = await action();

      if (owed != null) setChallenge(owed);
    } catch (failure) {
      // The API sends a code, never prose. Turning it into words here is what keeps
      // one backend able to serve both languages.
      setError(
        failure instanceof ApiError
          ? t(errorKey(failure.problem), {
              ...failure.problem.arguments,
              defaultValue: t('errors.unknown'),
            })
          : t('errors.unknown'),
      );
    } finally {
      setBusy(false);
    }
  }

  function onCompanySubmit(event: FormEvent) {
    event.preventDefault();
    void run(() => resolveTenant(company.trim().toLowerCase()));
  }

  function onCredentialsSubmit(event: FormEvent) {
    event.preventDefault();

    void run(() => signIn(email.trim(), password));
  }

  // Ahead of everything else: the password was right, and nothing else on this screen is
  // relevant until the code is in. There is deliberately no way past it — section 06 calls
  // MFA mandatory for these accounts, and a dismissable one is a checkbox.
  if (challenge !== null) {
    return (
      <SecondFactor
        challenge={challenge}
        prefix="/api/auth"
        onSignedIn={(session) => completeSecondFactor(session)}
        onCancel={() => {
          setChallenge(null);
          setPassword('');
        }}
      />
    );
  }

  return (
    <main className="min-h-screen grid place-items-center p-6">
      <Card className="w-[min(360px,100%)]">
        <form
          className="flex flex-col gap-3.5 p-6"
          onSubmit={wantsCredentials ? onCredentialsSubmit : onCompanySubmit}
        >
          <PageHeader title={branding?.displayName ?? t('signIn.title')} />

          {/* Why the form is showing, when nobody asked for it. Above the fields, because it
              is the first thing somebody returning to this screen needs to read; gone once
              a sign-in fails, when the failure is the more useful sentence. */}
          {sessionExpired && error === null && (
            <Banner tone="warn">{t('errors.auth.session_expired')}</Banner>
          )}

          {!wantsCredentials ? (
            <Field label={t('signIn.company')}>
              <Input
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                placeholder={t('signIn.companyHint')}
                autoComplete="organization"
                autoFocus
                required
              />
            </Field>
          ) : (
            <>
              <Field label={t('signIn.email')}>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="username"
                  autoFocus
                  required
                />
              </Field>

              <Field label={t('signIn.password')}>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                />
              </Field>
            </>
          )}

          {/* Announced to assistive technology — the Banner carries role="alert" for the
              danger tone: a dispatcher who cannot see the change still needs to know the
              sign-in failed. */}
          {error !== null && <Banner tone="danger">{error}</Banner>}

          <Button type="submit" variant="primary" disabled={busy} className="w-full">
            {busy ? t('signIn.working') : t('signIn.submit')}
          </Button>
        </form>
      </Card>
    </main>
  );
}
